import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from "@azure/msal-react";
import Dropdown from "../../Components/DropDownBox/DropdownV2";
// import CommentBox from '../CommentBox/CommentBox';
// import buttonStyles from '../YesNoButtonsCSS/YesNoButtons.module.css';
import { CircularProgress } from "@mui/material";
import styles from "../../Components/FeePropFeePopUp/FeePropFeePopUp.module.css";
import SearchBar from "../../Components/SearchBar/SearchBar";
import buttonStyles from "../../Components/YesNoButtonsCSS/YesNoButtons.module.css";
import stylesSearch from "../../Components/SearchBar/SearchBar.module.css";
import { handleSubmitFee, completeTask } from "../../redux/slices/dashboard";
import { useDispatch, useSelector } from "../../redux/store";
import { VariantType, useSnackbar } from "notistack";
import { addBillPayerToProject, fetchRecentBillPayers, searchBillPayers } from "../../redux/slices/projectBilling";

// Custom styles for client type tags
const tagStyles = {
  tag: {
    backgroundColor: '#F37021',
    color: '#FFFFFF',
    borderRadius: '4px',
    fontWeight: 'bold',
    fontSize: '9px',
    paddingLeft: '6px',
    paddingRight: '6px',
    height: '16px',
    textAlign: 'center',
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'center',
    marginRight: '10px',
    minWidth: '20px',
    flexShrink: 0,
  },
  optionContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    fontFamily: "'PT Sans', sans-serif",
    fontSize: '13px',
    fontWeight: 'bold',
  },
  optionText: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }
};

const AddBillPayerPopUp = ({ isVisible, onClose, jobNumber, onRefresh }) => {
  const { accounts } = useMsal();
  const account = accounts[0];
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [fee, setFee] = useState(0);
  const [comment, setComment] = useState("");
  const [suitableRunner, setSuitableRunner] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [billPayerOptions, setBillPayerOptions] = useState([]);
  const [optionsAvailable, setOptionsAvailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [clientTypeFilter, setClientTypeFilter] = useState("All"); // All, Professional, Homeowner
  const [recentBillPayers, setRecentBillPayers] = useState([]);

  // Fetch recent/popular bill payers on component mount
  useEffect(() => {
    const loadRecentBillPayers = async () => {
      try {
        const response = await dispatch(fetchRecentBillPayers()).unwrap();
        if (response?.data?.length > 0) {
          setRecentBillPayers(
            response.data.map((billPayer) => ({
              id: billPayer.billPayerID,
              value: billPayer.billPayerName,
              label: billPayer.billPayerName,
              clientType: billPayer.clientType,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching recent bill payers:", error);
      }
    };

    if (isVisible) {
      loadRecentBillPayers();
    }
  }, [isVisible, dispatch]);

  const debounceSearch = useCallback(() => {
      if (!inputValue.trim()) {
        // If no search term, show recent bill payers filtered by client type
        const filteredRecent = recentBillPayers.filter(bp => 
          clientTypeFilter === "All" || bp.clientType === clientTypeFilter
        );
        setBillPayerOptions(filteredRecent);
        setOptionsAvailable(filteredRecent.length > 0);
        return;
      }

      const fetchBillPayers = async () => {
          setLoading(true);
          try {
              const response = await dispatch(searchBillPayers({ billPayer: inputValue, clientType: clientTypeFilter })).unwrap();
              if (response?.data?.length > 0) {
                  const filteredResults = response.data
                    .filter(bp => clientTypeFilter === "All" || bp.clientType === clientTypeFilter)
                    .map((billPayer) => ({
                      id: billPayer.billPayerID,
                      value: billPayer.billPayerName,
                      label: billPayer.billPayerName,
                      clientType: billPayer.clientType,
                    }));
                  setBillPayerOptions(filteredResults);
                  setOptionsAvailable(filteredResults.length > 0);
              } else {
                setBillPayerOptions([]);
                setOptionsAvailable(false);
              }
          } catch (error) {
              console.error("Error fetching bill payers:", error);
          } finally {
              setLoading(false);
          }
      };

      fetchBillPayers();
  }, [inputValue, clientTypeFilter, recentBillPayers]);

  useEffect(() => {
      const handler = setTimeout(() => {
          debounceSearch();
      }, 500); // Debounce time in milliseconds

      return () => {
          clearTimeout(handler);
      };
  }, [debounceSearch]);

  if (!isVisible) return null;

  const handleSearchChange = (event) => {
      setInputValue(event.target.value);
  };

  const handleSearchSelection = (value) => {
      setInputValue(value.label);
      setSelectedData(value);
      console.log(value, "selected value");
      setOptionsAvailable(false);
  };

  const handleSearchSubmit = () => {
      setBillPayerOptions([]);
      if (!inputValue) {
          return;
      }
      debounceSearch();
  };

  const handleClientTypeFilterChange = (newFilter) => {
    setClientTypeFilter(newFilter);
    // Trigger search with new filter
    if (inputValue.trim()) {
      debounceSearch();
    } else {
      // Show filtered recent bill payers
      const filteredRecent = recentBillPayers.filter(bp => 
        newFilter === "All" || bp.clientType === newFilter
      );
      setBillPayerOptions(filteredRecent);
      setOptionsAvailable(filteredRecent.length > 0);
    }
  };

  const handleSubmit = async () => {
      setLoading(true);
      const body = {
          billPayerID: selectedData.id,
          jobNumber,
          updatedBy: account.username,
      };
      try {
          const response = await dispatch(addBillPayerToProject({ body })).unwrap();
          if (response.success) {
            enqueueSnackbar("Request completed successfully!", {
              variant: "success",
            });
              onRefresh();
              onClose();
          }
      } catch (error) {
          console.error(error);
      } finally {
          setLoading(false);
      }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <p className={styles.text}>
          Please search the client you wish to add
        </p>
        
        {/* Client Type Filter Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '10px', 
          marginBottom: '15px', 
          justifyContent: 'center' 
        }}>
          {['All', 'Professional', 'Homeowner'].map(type => (
            <button
              key={type}
              onClick={() => handleClientTypeFilterChange(type)}
              style={{
                padding: '6px 12px',
                borderRadius: '4px',
                border: '1px solid #3C96D4',
                backgroundColor: clientTypeFilter === type ? '#3C96D4' : '#fff',
                color: clientTypeFilter === type ? '#fff' : '#3C96D4',
                fontSize: '12px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {type}
            </button>
          ))}
        </div>

        <div className={stylesSearch.commentModal_feeSection}>
          <div>
            <div
              style={{ 
                width: "300px",
                position: "relative"
              }}
              className={stylesSearch.searchContainer}
            >
              <input
                type="text"
                className={`${stylesSearch.ProjectSearch}`}
                placeholder={`Search ${clientTypeFilter === 'All' ? 'clients' : clientTypeFilter.toLowerCase() + 's'}...`}
                onFocus={(e) => (e.target.placeholder = "")}
                onBlur={(e) => {
                  if (!e.target.value) e.target.placeholder = `Search ${clientTypeFilter === 'All' ? 'clients' : clientTypeFilter.toLowerCase() + 's'}...`;
                }}
                onChange={handleSearchChange}
                value={inputValue}
              />
              <button
                onClick={handleSearchSubmit}
                className={`${stylesSearch.searchButton}`}
              >
                <span className={stylesSearch.searchIconPlaceholder}></span>
              </button>
              {optionsAvailable && (
                <div 
                  style={{
                    maxHeight: '200px',
                    width: '100%',
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    backgroundColor: '#fff',
                    border: '1px solid #3C96D4',
                    borderRadius: '8px',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    zIndex: 1000,
                    marginTop: '2px',
                  }}
                >
                  {!inputValue.trim() && billPayerOptions.length > 0 && (
                    <div style={{
                      padding: '8px 12px',
                      fontSize: '10px',
                      color: '#666',
                      backgroundColor: '#f5f5f5',
                      borderBottom: '1px solid #eee',
                      fontWeight: 'bold'
                    }}>
                      {clientTypeFilter === 'All' ? 'RECENT CLIENTS' : `RECENT ${clientTypeFilter.toUpperCase()}S`}
                    </div>
                  )}
                  {billPayerOptions.map((option, index) => (
                    <div
                      key={option.id}
                      style={{
                        ...tagStyles.optionContainer,
                        height: '35px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: '#fff',
                        transition: 'background-color 0.15s ease',
                        borderBottom: index === billPayerOptions.length - 1 ? 'none' : '1px solid #f5f5f5',
                      }}
                      onMouseDown={() => handleSearchSelection(option)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#EAF2F9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }}
                    >
                      <div style={{
                        ...tagStyles.tag,
                        backgroundColor: option.clientType === 'Professional' ? '#4CAF50' : '#FF9800'
                      }}>
                        {option.clientType === 'Professional' ? 'P' : 'HO'}
                      </div>
                      <span style={tagStyles.optionText}>{option.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {loading && (
              <div className="ProfileHolidayOverlay">
                <div
                  className="ProfileHolidayResponseBox"
                  style={{ borderRadius: "150px", minWidth: "160px" }}
                >
                  <CircularProgress size={65} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "25px",
          }}
        >
          <div style={{ width: "74px", height: "25px" }}>
            <button
              className={buttonStyles.YesButtonComponentStyling}
              onClick={handleSubmit}
            >
              Add
            </button>
          </div>
          <div style={{ width: "74px", height: "25px", marginLeft: "10px" }}>
            <button
              className={buttonStyles.NoButtonComponentStyling}
              onClick={onClose}
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBillPayerPopUp;

const statusOptions = [
  {
    value: "PROMOTED",
    label: "Promoted",
  },
  {
    value: "QUOTED",
    label: "Quoted",
  },
];



// function MyComponent() {
//   const [inputValue, setInputValue] = useState('');

//   useEffect(() => {
//     const debouncedHandleInput = debounce(handleInput, 300);

//     function handleInput() {
//       // Your code to handle input goes here
//       console.log('Input value:', inputValue);
//     }

//     const inputElement = document.getElementById('myInput');
//     inputElement.addEventListener('input', debouncedHandleInput);

//     return () => {
//       inputElement.removeEventListener('input', debouncedHandleInput);
//     };
//   }, [inputValue]); // Only re-run the effect if inputValue changes

//   function debounce(func, delay) {
//     let timeoutId;

//     return function() {
//       const context = this;
//       const args = arguments;

//       clearTimeout(timeoutId);

//       timeoutId = setTimeout(() => {
//         func.apply(context, args);
//       }, delay);
//     };
//   }

//   function handleChange(event) {
//     setInputValue(event.target.value);
//   }

//   return (
//     <input 
//       id="myInput"
//       type="text" 
//       value={inputValue}
//       onChange={handleChange}
//     />
//   );
// }
